import { GitBranch, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface ForkSchemaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The editable default fork name, `"<case> - <orig>"`. */
  defaultName: string
  /** Fork with the (trimmed) name; the captured edit is applied server-side. */
  onConfirm: (name: string) => Promise<void>
}

/** Warns that editing a shared (global) schema forks it into a private case copy,
 * and collects the editable fork name. */
export function ForkSchemaDialog({
  open,
  onOpenChange,
  defaultName,
  onConfirm,
}: ForkSchemaDialogProps) {
  const [name, setName] = useState(defaultName)
  const [busy, setBusy] = useState(false)

  // Reseed the editable name each time the dialog opens for a new fork.
  useEffect(() => {
    if (open) setName(defaultName)
  }, [open, defaultName])

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      await onConfirm(trimmed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Edit creates a case copy</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            This is a shared schema. Saving your change forks it into a private copy for this case
            and switches the case to use it — the shared schema is left untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="fork-schema-name" className="text-foreground">
            Name
          </Label>
          <Input
            id="fork-schema-name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submit()
            }}
            className="bg-muted border-border"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !name.trim()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <GitBranch className="h-4 w-4 mr-1" />
            )}
            Fork &amp; save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
